import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from './firebase';

export interface UserTrackingSession {
  billingPeriod: string;
  premiumSubscription: boolean;
  productFlavor: string;
  subscriptionPlan: string | null;
  userId: string;
}

export interface UserAnalytics {
  totalActiveUsers: number;
  premiumUsers: number;
  freeUsers: number;
  usersBySubscriptionPlan: Record<string, number>;
}

/**
 * Fetch user tracking sessions for a specific billing period and product flavor
 * @param billingPeriod - Billing period in YYYY-MM format (e.g., "2025-11")
 * @param productFlavor - Product flavor filter ("cargoTour" or "satlog")
 */
export const fetchUserTrackingSessions = async (
  billingPeriod: string,
  productFlavor: string
): Promise<UserTrackingSession[]> => {
  try {
    console.log(`Fetching user tracking for billing period: ${billingPeriod}, flavor: ${productFlavor}`);

    const userTrackingRef = collection(firestore, 'user_tracking');

    const q = query(
      userTrackingRef,
      where('billingPeriod', '==', billingPeriod),
      where('productFlavor', '==', productFlavor)
    );

    const querySnapshot = await getDocs(q);

    const sessions: UserTrackingSession[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      sessions.push({
        billingPeriod: data.billingPeriod,
        premiumSubscription: data.premiumSubscription ?? false,
        productFlavor: data.productFlavor,
        subscriptionPlan: data.subscriptionPlan || null,
        userId: data.userId,
      });
    });

    console.log(`Fetched ${sessions.length} user tracking sessions`);
    return sessions;
  } catch (error) {
    console.error('Error fetching user tracking sessions:', error);
    throw error;
  }
};

/**
 * Analyze user tracking sessions to compute analytics metrics
 */
export const analyzeUserTrackingSessions = (sessions: UserTrackingSession[]): UserAnalytics => {
  // Get unique users
  const uniqueUsers = new Map<string, UserTrackingSession>();

  sessions.forEach(session => {
    // Keep one session per user (they should have the same subscription info within a billing period)
    if (!uniqueUsers.has(session.userId)) {
      uniqueUsers.set(session.userId, session);
    }
  });

  const uniqueUserSessions = Array.from(uniqueUsers.values());

  // Count premium vs free users
  const premiumUsers = uniqueUserSessions.filter(s => s.premiumSubscription).length;
  const freeUsers = uniqueUserSessions.filter(s => !s.premiumSubscription).length;

  // Count users by subscription plan
  const usersBySubscriptionPlan: Record<string, number> = {};

  uniqueUserSessions.forEach(session => {
    const plan = session.subscriptionPlan || 'No subscription';
    usersBySubscriptionPlan[plan] = (usersBySubscriptionPlan[plan] || 0) + 1;
  });

  return {
    totalActiveUsers: uniqueUsers.size,
    premiumUsers,
    freeUsers,
    usersBySubscriptionPlan,
  };
};

/**
 * Generate billing period options for the last 12 months
 */
export const generateBillingPeriodOptions = (): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  const today = new Date();

  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }

  return options;
};
